import { Mail, ExternalLink } from "lucide-react";

/**
 * Iter-161 — small reusable helper that prefills a `mailto:` link to the Drive
 * folder owner so the user can request Editor access in one click. The owner
 * is unknown to the platform (external Workspace), so we just pre-fill the
 * subject + body and let the user pick the recipient in their mail client.
 *
 * Props:
 *  - folderUrl  : full https://drive.google.com/drive/folders/<id> URL (required)
 *  - missionTitle : free-form context line (e.g. "Mission ACME — Audit Q1")
 *  - kind         : human-readable artifact type ("purchase order", "invoice", "monthly CRA", ...)
 *  - testid       : data-testid prefix; we expose `${testid}-open-folder` and `${testid}-mailto`
 *  - className    : container class
 */
export default function DriveAccessHelper({ folderUrl, missionTitle = "", kind = "file", testid = "drive-access" , className = "" }) {
  if (!folderUrl) return null;
  const subject = encodeURIComponent(`Drive access request — ${missionTitle || "BAIM"}`);
  const body = encodeURIComponent(
    `Hi,\n\nCould you please grant the BAIM platform Editor access to this Drive folder so we can archive the ${kind} automatically?\n\nFolder: ${folderUrl}\n${missionTitle ? `Context: ${missionTitle}\n` : ""}\nThanks!`
  );
  return (
    <div className={`rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-2 ${className}`} data-testid={`${testid}-block`}>
      <div className="font-semibold">Need access to the Drive folder?</div>
      <div>The platform could not push the {kind} because it lacks Editor rights on the target folder. Open the folder, identify the owner, and email them in one click.</div>
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={folderUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testid}-open-folder`}
          className="inline-flex items-center gap-1 px-2 h-7 bg-white border border-amber-300 rounded text-amber-900 hover:bg-amber-100"
        >
          <ExternalLink size={11} /> Open the folder
        </a>
        <a
          href={`mailto:?subject=${subject}&body=${body}`}
          data-testid={`${testid}-mailto`}
          className="inline-flex items-center gap-1 px-2 h-7 bg-white border border-amber-300 rounded text-amber-900 hover:bg-amber-100"
        >
          <Mail size={11} /> Email the folder owner
        </a>
      </div>
    </div>
  );
}
